import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { Stethoscope, Plus, Trash2, FileText, ArrowLeft, Save, Send, CheckCircle2, Link2, ClipboardCheck, Printer, Bell, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const S_STATUS = {
  planned: { label: 'Geplant', cls: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'Laufend', cls: 'bg-amber-100 text-amber-700' },
  done: { label: 'Erledigt', cls: 'bg-emerald-100 text-emerald-700' },
};

// Zahnstatus-Bezeichnungen (identisch zum Zahnschema)
const TOOTH_STATUS = [
  { v: 'healthy', l: 'Gesund' }, { v: 'caries', l: 'Karies' }, { v: 'filling', l: 'Füllung' },
  { v: 'crown', l: 'Krone' }, { v: 'root_canal', l: 'Wurzelbehandlung' }, { v: 'bridge', l: 'Brücke' },
  { v: 'implant', l: 'Implantat' }, { v: 'missing', l: 'Fehlt' },
];
const STATUS_LABEL = Object.fromEntries(TOOTH_STATUS.map((s) => [s.v, s.l]));
// Sinnvolle Vorbelegung nach erfolgter Behandlung
const SUGGEST = { caries: 'filling', root_canal: 'crown' };

// YYYY-MM-DD + N Monate (Tag bei Monatsende gekappt)
const _addMonthsISO = (iso, months) => {
  const d = new Date(iso + 'T00:00:00');
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), lastDay));
  return target.toISOString().slice(0, 10);
};

const SessionCard = ({ session, index, tariffs, tpwFor, lineAmount, onChange, onInvoice, onRemove }) => {
  const [system, setSystem] = useState('DENTOTAR');
  const [selTariff, setSelTariff] = useState('');
  const [minutes, setMinutes] = useState('');
  const [qty, setQty] = useState('1');
  const [matDesc, setMatDesc] = useState('');
  const [matAmount, setMatAmount] = useState('');

  const forSystem = tariffs.filter((t) => t.system === system && t.active !== false);
  const selected = tariffs.find((t) => t.id === selTariff);
  const total = (session.uiItems || []).reduce((s, it) => s + lineAmount(it), 0) + (session.materials || []).reduce((s, m) => s + Number(m.amount || 0), 0);

  const addItem = () => {
    if (!selected) { toast.error('Position wählen'); return; }
    if (selected.is_time_based && !(Number(minutes) > 0)) { toast.error('Minuten erforderlich'); return; }
    onChange({ ...session, uiItems: [...(session.uiItems || []), { tariff: selected, minutes: selected.is_time_based ? Number(minutes) : null, quantity: selected.is_time_based ? null : Number(qty || 1) }] });
    setSelTariff(''); setMinutes(''); setQty('1');
  };
  const addMat = () => {
    if (!matDesc.trim() || !(Number(matAmount) > 0)) { toast.error('Material + Betrag'); return; }
    onChange({ ...session, materials: [...(session.materials || []), { description: matDesc.trim(), amount: Number(matAmount), tax_rate: 8.1 }] });
    setMatDesc(''); setMatAmount('');
  };

  const st = S_STATUS[session.status] || S_STATUS.planned;

  return (
    <Card className="border-l-4 border-l-teal-400" data-testid={`plan-session-${index}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]"><Label>Sitzung</Label><Input value={session.name} onChange={(e) => onChange({ ...session, name: e.target.value })} data-testid={`plan-session-name-${index}`} /></div>
          <div><Label>Datum</Label><Input type="date" value={session.planned_date || ''} onChange={(e) => onChange({ ...session, planned_date: e.target.value })} className="w-40" data-testid={`plan-session-date-${index}`} /></div>
          <div><Label>Status</Label>
            <Select value={session.status} onValueChange={(v) => onChange({ ...session, status: v })}>
              <SelectTrigger className="w-36" data-testid={`plan-session-status-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="planned">Geplant</SelectItem><SelectItem value="in_progress">Laufend</SelectItem><SelectItem value="done">Erledigt</SelectItem></SelectContent>
            </Select>
          </div>
          <Badge variant="outline" className={st.cls}>{st.label}</Badge>
          {!session.invoice_id && <Button size="icon" variant="ghost" className="text-slate-400" onClick={() => onRemove()} title="Sitzung entfernen" data-testid={`plan-session-remove-${index}`}><Trash2 size={16} /></Button>}
        </div>

        {(session.uiItems || []).length > 0 && (
          <div className="border rounded divide-y">
            {session.uiItems.map((it, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm" data-testid={`plan-s${index}-line-${i}`}>
                <span>{it.tariff.code} {it.tariff.description} <span className="text-slate-400">· {it.tariff.is_time_based ? `${it.minutes} Min.` : `${it.quantity}×`}</span></span>
                <span className="flex items-center gap-2"><span className="font-medium">CHF {lineAmount(it).toFixed(2)}</span>{!session.invoice_id && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onChange({ ...session, uiItems: session.uiItems.filter((_, j) => j !== i) })} data-testid={`plan-s${index}-line-del-${i}`}><Trash2 size={12} /></Button>}</span>
              </div>
            ))}
            {(session.materials || []).map((m, i) => (
              <div key={`m${i}`} className="flex items-center justify-between px-3 py-1.5 text-sm">
                <span>Material: {m.description}</span>
                <span className="flex items-center gap-2"><span className="font-medium">CHF {Number(m.amount).toFixed(2)}</span>{!session.invoice_id && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onChange({ ...session, materials: session.materials.filter((_, j) => j !== i) })} data-testid={`plan-s${index}-mat-del-${i}`}><Trash2 size={12} /></Button>}</span>
              </div>
            ))}
          </div>
        )}

        {!session.invoice_id && (
          <div className="flex flex-wrap items-end gap-2">
            <Select value={system} onValueChange={(v) => { setSystem(v); setSelTariff(''); }}>
              <SelectTrigger className="w-40" data-testid={`plan-s${index}-system`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="DENTOTAR">DENTOTAR</SelectItem><SelectItem value="TARIF222">Tarif 222</SelectItem></SelectContent>
            </Select>
            <Select value={selTariff} onValueChange={setSelTariff}>
              <SelectTrigger className="w-64" data-testid={`plan-s${index}-tariff`}><SelectValue placeholder="Position…" /></SelectTrigger>
              <SelectContent>
                {forSystem.map((t) => (<SelectItem key={t.id} value={t.id}>{t.code} · {t.description}{t.is_time_based ? ' ⏱' : ''}</SelectItem>))}
              </SelectContent>
            </Select>
            {selected?.is_time_based
              ? <Input type="number" placeholder="Min." value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-20" data-testid={`plan-s${index}-minutes`} />
              : <Input type="number" placeholder="Anz." value={qty} onChange={(e) => setQty(e.target.value)} className="w-20" data-testid={`plan-s${index}-qty`} />}
            <Button size="sm" onClick={addItem} data-testid={`plan-s${index}-add`}><Plus size={14} className="mr-1" />Position</Button>
            <div className="flex items-end gap-1 ml-2">
              <Input placeholder="Material" value={matDesc} onChange={(e) => setMatDesc(e.target.value)} className="w-32" />
              <Input type="number" placeholder="CHF" value={matAmount} onChange={(e) => setMatAmount(e.target.value)} className="w-20" />
              <Button size="sm" variant="outline" onClick={addMat}><Plus size={14} /></Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-1 border-t">
          <div className="text-sm font-semibold" data-testid={`plan-session-total-${index}`}>Sitzung: CHF {total.toFixed(2)}</div>
          {session.invoice_id
            ? <Badge className="bg-emerald-600" data-testid={`plan-session-invoiced-${index}`}>Verrechnet · {session.invoice_number}</Badge>
            : <Button size="sm" variant="outline" onClick={onInvoice} data-testid={`plan-session-invoice-${index}`}><FileText size={14} className="mr-1" />Teilrechnung</Button>}
        </div>
      </CardContent>
    </Card>
  );
};

const DentalTreatmentPlanPage = () => {
  const [customers, setCustomers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [settings, setSettings] = useState({ dentotar_tpw: 1.0, tarif222_tpw: 1.0 });
  const [plans, setPlans] = useState([]);
  const [newCustomer, setNewCustomer] = useState('');
  const [newTitle, setNewTitle] = useState('Behandlungsplan');
  const [plan, setPlan] = useState(null); // detail UI state
  const [wbOpen, setWbOpen] = useState(false);
  const [wbItems, setWbItems] = useState([]); // [{fdi, current, status, note}]
  const [wbSaving, setWbSaving] = useState(false);
  const [wbAuto, setWbAuto] = useState(false); // nach Verrechnung automatisch geöffnet
  const [instCount, setInstCount] = useState('3');
  const [instFirstDue, setInstFirstDue] = useState(() => new Date().toISOString().slice(0, 10));
  const [instInterval, setInstInterval] = useState('1');
  const [remInst, setRemInst] = useState(null); // {id, amount, due_date} für Erinnerung
  const [remChannel, setRemChannel] = useState('email');
  const [remSending, setRemSending] = useState(false);

  const tariffMap = useMemo(() => Object.fromEntries(tariffs.map((t) => [t.id, t])), [tariffs]);
  const tpwFor = (sys) => (sys === 'TARIF222' ? settings.tarif222_tpw : settings.dentotar_tpw);
  const lineAmount = useCallback((it) => {
    const t = it.tariff; if (!t) return 0;
    const unit = +(Number(t.tax_points || 0) * tpwFor(t.system)).toFixed(2);
    if (t.is_time_based) { const per = Math.max(1, Number(t.minutes_per_unit || 5)); return +(unit * Math.max(1, Math.ceil(Number(it.minutes || 0) / per))).toFixed(2); }
    return +(unit * Number(it.quantity || 1)).toFixed(2);
  }, [settings]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlans = useCallback(async () => {
    try { const r = await axios.get(`${API}/dental/billing/plans`); setPlans(r.data?.items || []); }
    catch { toast.error('Pläne konnten nicht geladen werden'); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.customers || []))).catch(() => {});
    axios.get(`${API}/dental/billing/tariffs`).then((r) => setTariffs(r.data?.items || [])).catch(() => {});
    axios.get(`${API}/dental/billing/settings`).then((r) => setSettings(r.data)).catch(() => {});
    loadPlans();
  }, [loadPlans]);

  const toUi = useCallback((p) => ({
    ...p,
    sessions: (p.sessions || []).map((s) => ({
      ...s,
      uiItems: (s.items || []).map((raw) => ({
        tariff: tariffMap[raw.tariff_id] || { id: raw.tariff_id, code: '?', description: '(Tarif entfernt)', system: 'DENTOTAR', tax_points: 0, is_time_based: !!raw.minutes, minutes_per_unit: 5 },
        minutes: raw.minutes,
        quantity: raw.quantity,
      })),
    })),
  }), [tariffMap]);

  const openPlan = async (id) => {
    try { const r = await axios.get(`${API}/dental/billing/plans/${id}`); setPlan(toUi(r.data)); }
    catch { toast.error('Plan konnte nicht geladen werden'); }
  };

  const createPlan = async () => {
    if (!newCustomer) { toast.error('Bitte Patient wählen'); return; }
    try {
      const r = await axios.post(`${API}/dental/billing/plans`, { customer_id: newCustomer, title: newTitle, sessions: [{ name: 'Sitzung 1', status: 'planned', items: [], materials: [] }] });
      toast.success('Behandlungsplan erstellt');
      setNewCustomer(''); setNewTitle('Behandlungsplan');
      await loadPlans();
      setPlan(toUi(r.data.plan));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erstellen fehlgeschlagen'); }
  };

  const toPayload = (p) => ({
    customer_id: p.customer_id,
    title: p.title,
    teeth: p.teeth || [],
    sessions: (p.sessions || []).map((s) => ({
      id: s.id, name: s.name, planned_date: s.planned_date || null, status: s.status,
      items: (s.uiItems || []).map((it) => ({ tariff_id: it.tariff.id, minutes: it.minutes, quantity: it.quantity })),
      materials: s.materials || [],
    })),
  });

  const savePlan = async (silent) => {
    const r = await axios.put(`${API}/dental/billing/plans/${plan.id}`, toPayload(plan));
    setPlan(toUi(r.data.plan));
    if (!silent) toast.success('Plan gespeichert');
    loadPlans();
    return r.data.plan;
  };

  const updateSession = (idx, updated) => setPlan({ ...plan, sessions: plan.sessions.map((s, i) => (i === idx ? updated : s)) });
  const addSession = () => setPlan({ ...plan, sessions: [...plan.sessions, { id: undefined, name: `Sitzung ${plan.sessions.length + 1}`, status: 'planned', uiItems: [], materials: [] }] });
  const removeSession = (idx) => setPlan({ ...plan, sessions: plan.sessions.filter((_, i) => i !== idx) });

  const invoiceSession = async (idx) => {
    try {
      const saved = await savePlan(true);
      const sid = saved.sessions[idx]?.id;
      const r = await axios.post(`${API}/dental/billing/plans/${plan.id}/sessions/${sid}/invoice`);
      toast.success(`Teilrechnung ${r.data?.invoice_number} erstellt`);
      await openPlan(plan.id);
      // Nach der Verrechnung direkt den passenden Statuswechsel vorschlagen.
      if ((saved.teeth || []).length > 0) openWriteback(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Teilrechnung fehlgeschlagen'); }
  };

  const makeEstimate = async () => {
    try {
      await savePlan(true);
      const r = await axios.post(`${API}/dental/billing/plans/${plan.id}/estimate`);
      const link = `${window.location.origin}/kv/${r.data.public_token}${r.data.tenant_slug ? `?tenant=${r.data.tenant_slug}` : ''}`;
      try { await navigator.clipboard.writeText(link); } catch { /* noop */ }
      toast.success(`Kostenvoranschlag ${r.data.kv_number} erstellt – Freigabe-Link kopiert`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const downloadPdf = async () => {
    try {
      await savePlan(true);
      const r = await axios.get(`${API}/dental/billing/plans/${plan.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Behandlungsplan_${(plan.customer_name || 'Patient').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'PDF konnte nicht erstellt werden'); }
  };

  const openWriteback = async (auto = false) => {
    try {
      const r = await axios.get(`${API}/dental/billing/plans/${plan.id}/chart`);
      const teeth = r.data?.teeth || {};
      const items = (plan.teeth || []).map((fdi) => {
        const cur = teeth[fdi]?.status || 'healthy';
        return { fdi, current: cur, status: SUGGEST[cur] || cur, note: teeth[fdi]?.note || '' };
      });
      setWbItems(items);
      setWbAuto(auto);
      setWbOpen(true);
    } catch { toast.error('Zahnschema konnte nicht geladen werden'); }
  };

  const submitWriteback = async () => {
    setWbSaving(true);
    try {
      const updates = wbItems.map((it) => ({ fdi: it.fdi, status: it.status, note: (it.note || '').trim() || null }));
      const r = await axios.post(`${API}/dental/billing/plans/${plan.id}/writeback`, { updates });
      toast.success(`Zahnschema aktualisiert – ${r.data?.updated || 0} Zähne`);
      setWbOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rückschreiben fehlgeschlagen'); }
    finally { setWbSaving(false); }
  };

  const planTotal = plan ? plan.sessions.reduce((s, sess) => s + ((sess.uiItems || []).reduce((a, it) => a + lineAmount(it), 0) + (sess.materials || []).reduce((a, m) => a + Number(m.amount || 0), 0)), 0) : 0;

  const createInstallments = async () => {
    const count = parseInt(instCount, 10);
    if (!(count >= 1)) { toast.error('Anzahl Raten erforderlich'); return; }
    try {
      await savePlan(true);
      await axios.post(`${API}/dental/billing/plans/${plan.id}/installments`, {
        count, first_due: instFirstDue, interval_months: Math.max(1, parseInt(instInterval, 10) || 1),
      });
      toast.success('Ratenplan erstellt');
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ratenplan fehlgeschlagen'); }
  };

  const toggleInstallment = async (iid, paid) => {
    try {
      await axios.post(`${API}/dental/billing/plans/${plan.id}/installments/${iid}/status`, { status: paid ? 'paid' : 'open' });
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Status fehlgeschlagen'); }
  };

  const removeInstallments = async () => {
    try {
      await axios.delete(`${API}/dental/billing/plans/${plan.id}/installments`);
      toast.success('Ratenplan entfernt');
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Entfernen fehlgeschlagen'); }
  };

  const setInstLocal = (i, field, value) => setPlan({
    ...plan, installments: plan.installments.map((x, j) => (j === i ? { ...x, [field]: value } : x)),
  });

  const saveInstallment = async (iid, patch) => {
    try {
      await axios.patch(`${API}/dental/billing/plans/${plan.id}/installments/${iid}`, patch);
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rate konnte nicht gespeichert werden'); await openPlan(plan.id); }
  };

  const addInstallment = async () => {
    const nextDue = (plan.installments || []).reduce((mx, it) => (it.due_date > mx ? it.due_date : mx), new Date().toISOString().slice(0, 10));
    try {
      await axios.post(`${API}/dental/billing/plans/${plan.id}/installments/add`, {
        amount: Math.max(0.05, Number((plan.installment_summary?.open || 0).toFixed(2)) || 50), due_date: _addMonthsISO(nextDue, 1),
      });
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rate konnte nicht hinzugefügt werden'); }
  };

  const deleteInstallment = async (iid) => {
    try {
      await axios.delete(`${API}/dental/billing/plans/${plan.id}/installments/${iid}`);
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rate konnte nicht entfernt werden'); }
  };

  const sendReminder = async () => {
    setRemSending(true);
    try {
      const r = await axios.post(`${API}/dental/billing/plans/${plan.id}/installments/${remInst.id}/remind`, { channel: remChannel });
      toast.success(r.data?.message || 'Erinnerung gesendet');
      setRemInst(null);
      await openPlan(plan.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erinnerung fehlgeschlagen'); }
    finally { setRemSending(false); }
  };

  // ── Detailansicht ──
  if (plan) {
    const done = plan.sessions.filter((s) => s.status === 'done').length;
    return (
      <div className="space-y-5" data-testid="dental-plan-detail">
        <Button variant="ghost" onClick={() => { setPlan(null); loadPlans(); }} data-testid="plan-back"><ArrowLeft size={16} className="mr-1" /> Zurück zur Übersicht</Button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label>Titel</Label>
            <Input value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} className="text-lg font-semibold" data-testid="plan-title" />
            <div className="text-sm text-slate-500 mt-1">{plan.customer_name} · {done}/{plan.sessions.length} Sitzungen erledigt</div>
            {plan.chart_summary ? (
              <div className="mt-2 text-xs text-slate-600 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2" data-testid="plan-chart-summary">
                <span className="font-semibold text-teal-700">Aus Zahnschema:</span> {plan.chart_summary}
              </div>
            ) : null}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold" data-testid="plan-total">CHF {planTotal.toFixed(2)}</div>
            {plan.invoiced_total > 0 && <div className="text-xs text-emerald-600">davon verrechnet: CHF {Number(plan.invoiced_total).toFixed(2)}</div>}
          </div>
        </div>

        <div className="space-y-3">
          {plan.sessions.map((s, i) => (
            <SessionCard key={s.id || i} session={s} index={i} tariffs={tariffs} tpwFor={tpwFor} lineAmount={lineAmount}
              onChange={(u) => updateSession(i, u)} onInvoice={() => invoiceSession(i)} onRemove={() => removeSession(i)} />
          ))}
        </div>

        <Card data-testid="installment-card" className="border-l-4 border-l-indigo-400">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><FileText size={16} /> Ratenzahlung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(plan.installments || []).length === 0 ? (
              <div className="flex flex-wrap items-end gap-3" data-testid="installment-generator">
                <div>
                  <Label className="text-xs">Anzahl Raten</Label>
                  <Input type="number" min="1" value={instCount} onChange={(e) => setInstCount(e.target.value)} className="w-24" data-testid="installment-count" />
                </div>
                <div>
                  <Label className="text-xs">Erste Fälligkeit</Label>
                  <Input type="date" value={instFirstDue} onChange={(e) => setInstFirstDue(e.target.value)} className="w-40" data-testid="installment-first-due" />
                </div>
                <div>
                  <Label className="text-xs">Intervall (Monate)</Label>
                  <Input type="number" min="1" value={instInterval} onChange={(e) => setInstInterval(e.target.value)} className="w-24" data-testid="installment-interval" />
                </div>
                <Button onClick={createInstallments} data-testid="installment-create"><Plus size={16} className="mr-1" /> Ratenplan erstellen</Button>
                <span className="text-xs text-slate-400 self-center">Teilt CHF {planTotal.toFixed(2)} gleichmässig auf.</span>
              </div>
            ) : (
              <>
                {plan.installment_summary && (
                  <div className="flex flex-wrap gap-4 text-sm" data-testid="installment-summary">
                    <span>Bezahlt: <b className="text-emerald-600">CHF {Number(plan.installment_summary.paid).toFixed(2)}</b></span>
                    <span>Offen: <b className="text-amber-600">CHF {Number(plan.installment_summary.open).toFixed(2)}</b></span>
                    {plan.installment_summary.overdue_count > 0 && (
                      <span className="text-rose-600">Überfällig: <b>{plan.installment_summary.overdue_count}</b></span>
                    )}
                  </div>
                )}
                {plan.installment_summary && Math.abs(Number(plan.installment_summary.total) - (Number(plan.total ?? planTotal))) > 0.01 && (
                  <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="installment-sum-warning">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>Die Summe der Raten (<b>CHF {Number(plan.installment_summary.total).toFixed(2)}</b>) weicht vom Plantotal (<b>CHF {Number(plan.total ?? planTotal).toFixed(2)}</b>) ab. Bitte Beträge prüfen.</span>
                  </div>
                )}
                <div className="space-y-2">
                  {(plan.installments || []).map((it, i) => {
                    const overdue = it.status !== 'paid' && it.due_date && it.due_date < new Date().toISOString().slice(0, 10);
                    const paid = it.status === 'paid';
                    return (
                      <div key={it.id} className="space-y-1">
                      <div className="flex flex-wrap items-center gap-3 border rounded-lg px-3 py-2" data-testid={`installment-row-${i}`}>
                        <span className="font-semibold text-slate-700 w-16">Rate {i + 1}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400">CHF</span>
                          <Input type="number" step="0.05" min="0.05" value={it.amount}
                            disabled={paid}
                            onChange={(e) => setInstLocal(i, 'amount', e.target.value)}
                            onBlur={(e) => { const v = Number(e.target.value); if (v > 0) saveInstallment(it.id, { amount: v }); }}
                            className="w-24 h-8 tabular-nums" data-testid={`installment-amount-${i}`} />
                        </div>
                        <Input type="date" value={it.due_date || ''}
                          disabled={paid}
                          onChange={(e) => setInstLocal(i, 'due_date', e.target.value)}
                          onBlur={(e) => { if (e.target.value) saveInstallment(it.id, { due_date: e.target.value }); }}
                          className="w-40 h-8" data-testid={`installment-due-${i}`} />
                        {paid
                          ? <Badge className="bg-emerald-100 text-emerald-700">Bezahlt{it.paid_at ? ` · ${it.paid_at}` : ''}</Badge>
                          : overdue ? <Badge className="bg-rose-100 text-rose-700">Überfällig</Badge>
                          : <Badge className="bg-slate-100 text-slate-600">Offen</Badge>}
                        <div className="ml-auto flex items-center gap-1">
                          {!paid && (
                            <Button size="sm" variant="ghost" className="text-indigo-600 hover:bg-indigo-50" onClick={() => { setRemInst(it); setRemChannel('email'); }} data-testid={`installment-remind-${i}`}><Bell size={14} className="mr-1" /> Erinnern</Button>
                          )}
                          <Button size="sm" variant={paid ? 'ghost' : 'outline'} onClick={() => toggleInstallment(it.id, !paid)} data-testid={`installment-toggle-${i}`}>
                            {paid ? 'Als offen' : <><CheckCircle2 size={14} className="mr-1" /> Bezahlt</>}
                          </Button>
                          <Button size="sm" variant="ghost" className="text-rose-500 hover:bg-rose-50" onClick={() => deleteInstallment(it.id)} data-testid={`installment-delete-${i}`}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                      {it.reminded_at && !paid ? (
                        <div className="text-xs text-slate-400 pl-16 -mt-1" data-testid={`installment-reminded-${i}`}>Erinnert am {String(it.reminded_at).slice(0, 10)} ({it.reminder_channel === 'whatsapp' ? 'WhatsApp' : 'E-Mail'})</div>
                      ) : null}
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={addInstallment} data-testid="installment-add"><Plus size={14} className="mr-1" /> Rate hinzufügen</Button>
                  <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={removeInstallments} data-testid="installment-remove"><Trash2 size={14} className="mr-1" /> Ratenplan entfernen</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={addSession} data-testid="plan-add-session"><Plus size={16} className="mr-1" /> Sitzung hinzufügen</Button>
          <Button onClick={() => savePlan(false)} data-testid="plan-save"><Save size={16} className="mr-1" /> Plan speichern</Button>
          <Button variant="outline" onClick={makeEstimate} data-testid="plan-estimate"><Send size={16} className="mr-1" /> KV für Freigabe erstellen</Button>
          <Button variant="outline" onClick={downloadPdf} data-testid="plan-pdf"><Printer size={16} className="mr-1" /> Plan als PDF</Button>
          {(plan.teeth || []).length > 0 && (
            <Button variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" onClick={() => openWriteback(false)} data-testid="plan-writeback"><ClipboardCheck size={16} className="mr-1" /> Status ins Zahnschema zurückschreiben</Button>
          )}
        </div>

        <Dialog open={wbOpen} onOpenChange={setWbOpen}>
          <DialogContent className="max-w-lg" data-testid="writeback-dialog">
            <DialogHeader><DialogTitle>Status ins Zahnschema zurückschreiben</DialogTitle></DialogHeader>
            {wbAuto
              ? <p className="text-sm text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 -mt-2" data-testid="writeback-auto-hint">Sitzung verrechnet – bitte den neuen Befund je Zahn bestätigen. Die Änderung wird als Besuchs-Snapshot versioniert.</p>
              : <p className="text-sm text-slate-500 -mt-2">Neuer Befund je Zahn nach der Behandlung. Die Änderung wird als Besuchs-Snapshot versioniert.</p>}
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {wbItems.map((it, i) => (
                <div key={it.fdi} className="flex flex-wrap items-center gap-2 border rounded-lg px-3 py-2" data-testid={`writeback-row-${it.fdi}`}>
                  <span className="font-semibold text-slate-900 w-10">{it.fdi}</span>
                  <span className="text-xs text-slate-400 w-28">vorher: {STATUS_LABEL[it.current] || it.current}</span>
                  <Select value={it.status} onValueChange={(v) => setWbItems(wbItems.map((x, j) => (j === i ? { ...x, status: v } : x)))}>
                    <SelectTrigger className="w-40" data-testid={`writeback-status-${it.fdi}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{TOOTH_STATUS.map((s) => (<SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>))}</SelectContent>
                  </Select>
                  <Input placeholder="Notiz" value={it.note} onChange={(e) => setWbItems(wbItems.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} className="flex-1 min-w-[120px]" data-testid={`writeback-note-${it.fdi}`} />
                </div>
              ))}
              {wbItems.length === 0 && <div className="text-center text-slate-400 py-4">Keine Zähne im Plan</div>}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setWbOpen(false)} data-testid="writeback-cancel">{wbAuto ? 'Später' : 'Abbrechen'}</Button>
              <Button onClick={submitWriteback} disabled={wbSaving || wbItems.length === 0} data-testid="writeback-save"><ClipboardCheck size={16} className="mr-1" /> Übernehmen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!remInst} onOpenChange={(o) => { if (!o) setRemInst(null); }}>
          <DialogContent className="max-w-md" data-testid="reminder-dialog">
            <DialogHeader><DialogTitle>Zahlungserinnerung senden</DialogTitle></DialogHeader>
            {remInst ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 -mt-2">Erinnerung an <b>{plan.customer_name}</b> für die Rate über CHF {Number(remInst.amount).toFixed(2)} (fällig {remInst.due_date}). Der Versand erfolgt erst mit Ihrer Bestätigung.</p>
                <div>
                  <Label className="text-xs">Kanal</Label>
                  <Select value={remChannel} onValueChange={setRemChannel}>
                    <SelectTrigger data-testid="reminder-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">E-Mail</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-400">Die Nachricht enthält Betrag, Fälligkeit und den Plantitel. Bei fehlender E-Mail/Telefonnummer wird der Versand abgelehnt.</p>
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRemInst(null)} data-testid="reminder-cancel">Abbrechen</Button>
              <Button onClick={sendReminder} disabled={remSending} data-testid="reminder-send"><Bell size={16} className="mr-1" /> Erinnerung senden</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Übersicht ──
  return (
    <div className="space-y-6" data-testid="dental-plans-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Stethoscope className="text-teal-600" size={24} /> Behandlungspläne</h1>
        <p className="text-sm text-slate-500">Mehrere Sitzungen planen, Fortschritt verfolgen und pro Sitzung Teilrechnungen erstellen.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Neuer Behandlungsplan</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]"><Label>Patient</Label><CustomerSearchSelect customers={customers} value={newCustomer} onValueChange={setNewCustomer} placeholder="Patient suchen..." /></div>
          <div className="flex-1 min-w-[200px]"><Label>Titel</Label><Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="plan-new-title" /></div>
          <Button onClick={createPlan} data-testid="plan-create"><Plus size={16} className="mr-1" /> Plan erstellen</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Alle Behandlungspläne</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {plans.length === 0 && <div className="text-center text-slate-400 py-6">Noch keine Behandlungspläne</div>}
          {plans.map((p) => (
            <button key={p.id} onClick={() => openPlan(p.id)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg border hover:bg-slate-50 text-left" data-testid={`plan-row-${p.id}`}>
              <div>
                <div className="font-medium text-slate-900">{p.title}</div>
                <div className="text-sm text-slate-500">{p.customer_name} · {p.progress?.done ?? 0}/{p.progress?.total ?? 0} Sitzungen</div>
              </div>
              <div className="text-right">
                <div className="font-semibold">CHF {Number(p.total).toFixed(2)}</div>
                {p.invoiced_total > 0 && <div className="text-xs text-emerald-600">verrechnet: CHF {Number(p.invoiced_total).toFixed(2)}</div>}
              </div>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default DentalTreatmentPlanPage;
